import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { getAllContactMessages, markContactMessageAsRead } from "../../../services/Admin/ContactMessageApi";
import { resolveImageUrl } from "../../../services/indexMediaBaseUrl";

const ContactMessagesPage = () => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [markingId, setMarkingId] = useState(null);

  const fetch = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getAllContactMessages();
      const data = res?.status || res?.success ? res.data : res;
      const list = Array.isArray(data) ? data : [];
      // sort newest -> oldest by created_at
      list.sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setMessages(list);
    } catch (err) {
      setError(err?.message || "فشل تحميل رسائل التواصل");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetch();
  }, []);

  const handleMarkAsRead = async (id) => {
    if (!id) return;
    setMarkingId(id);
    try {
      const res = await markContactMessageAsRead(id);
      // مرن: قبول res.success أو res.status
      if (res?.success || res?.status) {
        // حدّث الحالة محليًا بدل إعادة تحميل كامل القائمة
        setMessages((prev) => (Array.isArray(prev) ? prev.map((m) => (m.id === id ? { ...m, status: "read" } : m)) : prev));
        // لو كانت التفاصيل مفتوحة للرسالة نفسها، حدّثها أيضاً
        setSelected((s) => (s && s.id === id ? { ...s, status: "read" } : s));
      } else {
        throw new Error(res?.message || "فشل تمييز الرسالة كمقروءة");
      }
    } catch (err) {
      console.error(err);
      // بسيطة: عرض رسالة خطأ عبر alert أو تحديث حالة الخطأ
      alert(err?.message || "حدث خطأ أثناء تمييز الرسالة كمقروءة");
    } finally {
      setMarkingId(null);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : "-";

  // map message status -> Arabic label
  const STATUS_LABELS = {
    read: "مقروء",
    pending: "قيد الانتظار",
    unread: "غير مقروء",
    // keep other statuses if any
  };

  // helper: does message match query + apply status/date filters
  const matchesQuery = (m, q) => {
    // status filter
    if (statusFilter && statusFilter !== "all") {
      if (String(m.status) !== String(statusFilter)) return false;
    }
    // date range filter (created_at)
    if (dateFrom || dateTo) {
      const created = m.created_at ? new Date(m.created_at) : null;
      if (!created) return false;
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0,0,0,0);
        if (created < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23,59,59,999);
        if (created > to) return false;
      }
    }

    if (!q) return true;
    const s = String(q).trim().toLowerCase();
    const parts = [
      m.id,
      m.name,
      m.email,
      m.subject,
      m.message,
      m.file,
      m.status,
      STATUS_LABELS[m.status],
      m.response,
      m.created_at,
    ].filter(Boolean).map((v) => String(v).toLowerCase());
    return parts.some((p) => p.includes(s));
  };

  const filteredMessages = Array.isArray(messages) ? messages.filter((m) => matchesQuery(m, searchQuery)) : [];

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">رسائل التواصل</Typography>
            <Typography variant="body2" color="text.secondary">عرض رسائل العملاء الواردة</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">كل الحالات</MenuItem>
              {Object.keys(STATUS_LABELS).map((k) => (
                <MenuItem key={k} value={k}>{STATUS_LABELS[k]}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder="ابحث في الرسائل..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ minWidth: 300 }}
            />
            <TextField
              size="small"
              type="date"
              label="من"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label="إلى"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <Button startIcon={<RefreshIcon />} onClick={fetch} disabled={loading} variant="outlined" size="small">
              {loading ? "جارٍ..." : "تحديث"}
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !filteredMessages.length ? (
          <Typography color="text.secondary">{messages.length ? "لا توجد نتائج مطابقة" : "لا توجد رسائل"}</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>الاسم</TableCell>
                  <TableCell>الإيميل</TableCell>
                  <TableCell>الموضوع</TableCell>
                  <TableCell>تاريخ الإنشاء</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell align="center">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMessages.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.subject || "-"}</TableCell>
                    <TableCell>{formatDate(m.created_at)}</TableCell>
                    <TableCell>{(m.status && STATUS_LABELS[m.status]) ? STATUS_LABELS[m.status] : (m.status || "-")}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelected(m); setOpenDialog(true); }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => handleMarkAsRead(m.id)}
                        disabled={m.status === "read" || markingId === m.id}
                      >
                        {markingId === m.id ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تفاصيل الرسالة #{selected?.id || ""}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={1}>
              <Typography><strong>الاسم:</strong> {selected.name}</Typography>
              <Typography><strong>البريد:</strong> {selected.email}</Typography>
              <Typography><strong>الموضوع:</strong> {selected.subject}</Typography>
              <Typography><strong>الرسالة:</strong></Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{selected.message}</Typography>

              {/* ملف مرفق (إن وجد) */}
              {selected.file ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>مرفق</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    {/* رابط مُحلّل عبر resolveImageUrl */}
                    {resolveImageUrl ? resolveImageUrl(selected.file) : selected.file}
                  </Typography>
                  {/* معاينة إذا كانت صورة، وإلا زر فتح الملف */}
                  {/\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(selected.file) ? (
                    <Box sx={{ mt: 1 }}>
                      <img src={resolveImageUrl ? resolveImageUrl(selected.file) : selected.file} alt="attachment" style={{ maxWidth: "100%", maxHeight: 360, borderRadius: 6 }} />
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Button component="a" href={resolveImageUrl ? resolveImageUrl(selected.file) : selected.file} target="_blank" rel="noopener">فتح الملف</Button>
                    </Box>
                  )}
                </Box>
              ) : null}

              <Typography><strong>الحالة:</strong> {(selected?.status && STATUS_LABELS[selected.status]) ? STATUS_LABELS[selected.status] : (selected?.status ?? "-")}</Typography>
              <Typography><strong>الرد:</strong> {selected.response || "-"}</Typography>
              <Typography><strong>تاريخ الإنشاء:</strong> {formatDate(selected.created_at)}</Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContactMessagesPage;
